import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { GraduationCap, Star, Users, Link2, Clock, Heart, TrendingUp } from 'lucide-react';
import { centerToast } from '../../components/CenterToast';
import { getShareLink } from '../../utils/links';

const MY_STATS = [
  { label: 'Ihsan Points',       value: '240', icon: Star,          color: 'text-warning', bgColor: 'bg-warning/10' },
  { label: 'Volunteer hours',    value: '6',   icon: Clock,         color: 'text-primary', bgColor: 'bg-primary/10' },
  { label: 'Courses completed',  value: '2',   icon: GraduationCap, color: 'text-success', bgColor: 'bg-success/10' },
  { label: 'Services accessed',  value: '3',   icon: Heart,         color: 'text-accent',  bgColor: 'bg-accent/10' }
];

const COMMUNITY_STATS = [
  { label: 'Families supported', value: '5,400+', icon: Heart,         color: 'text-primary', bgColor: 'bg-primary/10' },
  { label: 'Volunteer hours',    value: '3,240+', icon: Clock,         color: 'text-accent',  bgColor: 'bg-accent/10' },
  { label: 'Courses completed',  value: '620',    icon: GraduationCap, color: 'text-success', bgColor: 'bg-success/10' }
];

const SectionLabel = ({ children }) => (
  <p className="px-4 pt-6 pb-2 text-[12px] uppercase tracking-wider font-semibold text-muted-foreground">
    {children}
  </p>
);

export default function Impact() {
  const referralsMade = 4;
  const referralGoal = 5;
  const referralPct = Math.round((referralsMade / referralGoal) * 100);
  const remaining = Math.max(0, referralGoal - referralsMade);

  const handleCopyLink = () => {
    const link = getShareLink('referral', { slug: 'aminah-42x' });
    if (navigator.clipboard) navigator.clipboard.writeText(link);
    centerToast('Link copied! Share it on WhatsApp or Telegram.', '🔗');
  };

  return (
    <div className="min-h-screen bg-background page-fade-in pb-8">
      {/* Header */}
      <div className="bg-card border-b px-4 py-6">
        <h1 className="text-3xl font-bold mb-2">Your Impact</h1>
        <p className="text-muted-foreground">Your effort, your reach, our community.</p>
      </div>

      {/* LAYER 1 — MY CONTRIBUTION */}
      <SectionLabel>What I&apos;ve done</SectionLabel>
      <div className="px-4 grid grid-cols-2 gap-3" data-testid="my-contribution">
        {MY_STATS.map((s, i) => {
          const Icon = s.icon;
          return (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground leading-tight">{s.label}</span>
                  <div className={`w-8 h-8 rounded-lg ${s.bgColor} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`h-4 w-4 ${s.color}`} />
                  </div>
                </div>
                <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* LAYER 2 — MY REFERRALS' CONTRIBUTION */}
      <SectionLabel>What my referrals have done</SectionLabel>
      <div className="px-4" data-testid="my-referrals">
        <Card>
          <CardContent className="p-5 space-y-5">
            <div className="flex items-start gap-5">
              {/* Left: big number */}
              <div className="flex-shrink-0 text-center">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mb-2">
                  <Users className="h-7 w-7 text-primary" />
                </div>
                <div className="text-4xl font-bold text-primary leading-none">{referralsMade}</div>
                <p className="text-[11px] text-muted-foreground leading-tight mt-1 max-w-[80px]">
                  people you&apos;ve brought in
                </p>
              </div>

              {/* Right: stacked small stats */}
              <div className="flex-1 space-y-2.5 pt-1 min-w-0">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground truncate">Their volunteer hours</span>
                  <span className="font-semibold">14</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground truncate">Their courses completed</span>
                  <span className="font-semibold">5</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground truncate">Their services accessed</span>
                  <span className="font-semibold">8</span>
                </div>
              </div>
            </div>

            {/* Referral goal progress */}
            <div className="pt-2 border-t space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">
                  {referralsMade} of {referralGoal} referrals for Community Connector badge
                </span>
                <span className="font-semibold text-primary">{referralPct}%</span>
              </div>
              <div
                className="relative h-2.5 w-full rounded-full bg-muted overflow-hidden"
                role="progressbar"
                aria-valuenow={referralPct}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="h-full bg-primary rounded-full transition-all duration-500"
                  style={{ width: `${referralPct}%` }}
                />
              </div>

              {/* Nudge shown while goal is almost reached */}
              {remaining > 0 && remaining <= 2 && (
                <div
                  data-testid="badge-nudge"
                  className="mt-3 flex items-center gap-3 rounded-lg bg-primary/10 border border-primary/25 px-3 py-2.5"
                >
                  <span className="text-lg leading-none" aria-hidden="true">🎯</span>
                  <p className="flex-1 text-xs sm:text-sm font-medium leading-snug">
                    Just <span className="text-primary font-bold">{remaining} more</span> to earn Community Connector
                  </p>
                  <Button
                    data-testid="badge-nudge-cta"
                    onClick={handleCopyLink}
                    size="sm"
                    className="h-8 px-3 text-xs gap-1.5 flex-shrink-0"
                  >
                    <Link2 className="h-3.5 w-3.5" />
                    Copy link
                  </Button>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* LAYER 3 — COMBINED + COMMUNITY */}
      <SectionLabel>Our collective impact</SectionLabel>
      <div className="px-4 space-y-3" data-testid="collective-impact">
        {/* Row 1: My total contribution */}
        <Card className="border-l-4 border-l-primary bg-gradient-to-r from-primary/10 to-transparent">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="h-4 w-4 text-primary" />
              <p className="text-sm font-semibold">My total contribution</p>
              <span className="text-[11px] text-muted-foreground">(me + referrals)</span>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Total Ihsan Points</p>
                <p className="text-2xl font-bold text-primary">890</p>
              </div>
              <div>
                <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Total hours</p>
                <p className="text-2xl font-bold text-primary">20</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Row 2: MTFA community total */}
        <Card>
          <CardContent className="p-5">
            <p className="text-sm font-semibold mb-3">MTFA community total</p>
            <div className="grid grid-cols-3 gap-3">
              {COMMUNITY_STATS.map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} className="text-center">
                    <div className={`w-9 h-9 rounded-full ${s.bgColor} flex items-center justify-center mx-auto mb-1.5`}>
                      <Icon className={`h-4 w-4 ${s.color}`} />
                    </div>
                    <p className={`text-base font-bold ${s.color} leading-none`}>{s.value}</p>
                    <p className="text-[10px] text-muted-foreground leading-tight mt-1">{s.label}</p>
                  </div>
                );
              })}
            </div>
            <p className="text-xs text-muted-foreground text-center mt-4 italic">
              You and your network are part of this.
            </p>
          </CardContent>
        </Card>
      </div>

      {/* REFERRAL CTA */}
      <div className="px-4 pt-6" data-testid="referral-cta">
        <div className="border-2 border-dashed border-primary/50 rounded-xl bg-primary/5 p-5 space-y-3 text-center">
          <div className="w-12 h-12 rounded-full bg-primary/15 flex items-center justify-center mx-auto">
            <Link2 className="h-6 w-6 text-primary" />
          </div>
          <h3 className="font-semibold text-base">Refer someone today</h3>
          <p className="text-sm text-muted-foreground leading-relaxed max-w-md mx-auto">
            Share your personal link. Every person you bring earns you Ihsan Points and grows our community.
          </p>
          <Button
            data-testid="copy-referral-link"
            onClick={handleCopyLink}
            size="lg"
            className="w-full h-11"
          >
            <Link2 className="h-4 w-4 mr-2" />
            Copy my referral link
          </Button>
        </div>
      </div>
    </div>
  );
}
